/**
 * PackshotImageCard - Klickbare Bild-Karte mit Score und Kategorie
 *
 * Wird im PackshotPickerDialog pro analysiertem Bild gerendert.
 * Ausgewählt → primary-500 Ring + Checkmark.
 */

import { Check, ImageIcon } from 'lucide-react'
import type { PackshotCandidate } from '@/lib/api/image-intelligence'

const SCORE_CLASSES: Record<string, string> = {
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
}

function scoreVariant(score: number): string {
  if (score >= 70) return 'emerald'
  if (score >= 40) return 'amber'
  return 'red'
}

interface PackshotImageCardProps {
  candidate: PackshotCandidate
  selected: boolean
  isCurrent?: boolean
  onClick: () => void
}

export function PackshotImageCard({ candidate, selected, isCurrent, onClick }: PackshotImageCardProps) {
  const score = Math.round(candidate.overall_score)
  const variant = scoreVariant(score)

  const borderClass = selected
    ? 'ring-2 ring-primary-500 border-primary-300 bg-primary-50 dark:bg-primary-900/20'
    : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-800/50'

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      aria-label={`${candidate.category} auswählen, Score ${score}`}
      className={`packshot-image-card relative flex flex-col items-center gap-1.5 p-2 rounded-xl border transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${borderClass}`}
    >
      {selected && (
        <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
          <Check className="w-3 h-3 text-white" aria-hidden="true" />
        </div>
      )}
      {isCurrent && !selected && (
        <span className="absolute top-1 right-1 text-3xs text-primary-500 font-semibold uppercase leading-none">
          Aktuell
        </span>
      )}
      {candidate.thumbnail_url ? (
        <img
          src={candidate.thumbnail_url}
          alt={candidate.category}
          className="w-16 h-16 rounded-lg object-cover"
        />
      ) : (
        <div className="w-16 h-16 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
          <ImageIcon className="h-6 w-6 text-neutral-400" aria-hidden="true" />
        </div>
      )}
      <span className={`text-xs font-bold tabular-nums px-1.5 py-0.5 rounded ${SCORE_CLASSES[variant]}`}>
        {score}
      </span>
      <span className="text-2xs text-neutral-500 uppercase tracking-wider leading-none">
        {candidate.category}
      </span>
    </button>
  )
}
